<template>
    <div class="dv2-section">
        <SectionHeader icon="request_quote" title="Cotações" anchor="sec-resultado" />

        <div class="dv2-grid dv2-grid--kpi-chart">
            <KpiCard title="Cotações e pedidos" :columns="2" :loading="loading">
                <KpiTile
                    v-for="tile in quotesKpis"
                    :key="tile.label"
                    v-bind="tile"
                />
            </KpiCard>

            <HighchartsCard
                title="Cotações por status por mês"
                :tooltip="tooltips.status"
                :loading="loading"
                :options="statusOptions"
            />
        </div>

        <div class="dv2-grid dv2-grid--chart-kpi">
            <HighchartsCard
                title="Histórico de qualidade da cotação por mês"
                :tooltip="tooltips.quality"
                :loading="loading"
                :options="qualityOptions"
            />

            <KpiCard
                title="Qualidade da cotação"
                :columns="3"
                :loading="loading"
            >
                <KpiTile
                    v-for="tile in qualityKpis"
                    :key="tile.label"
                    v-bind="tile"
                />
            </KpiCard>
        </div>
    </div>
</template>

<script>
import SectionHeader from "./SectionHeader.vue";
import KpiCard from "./KpiCard.vue";
import KpiTile from "./KpiTile.vue";
import HighchartsCard from "./HighchartsCard.vue";
import DashboardV2Service from "@/services/v3/dashboard/DashboardV2Service";
import {
    baseTheme,
    xAxisDefaults,
    yAxisDefaults,
    pluck,
} from "../shared/highchartsTheme";
import { BRAND } from "../shared/constants";
import { formatMonthLabel } from "../shared/format";
import { buildKpiTiles } from "../shared/kpi";

const service = new DashboardV2Service();

// Textos fixos de apoio (tooltips dos cartões de gráfico) — copy da UI,
// não dado, por isso vive aqui e não no mock.
const TOOLTIPS = {
    status: "Colunas empilhadas: cotações por status (fechadas, em andamento, congeladas) por mês — a soma é o total de cotações.",
    quality:
        "Colunas: médias por cotação (concorrentes, produtos, oferta). Linhas no eixo direito: engajamento do vendedor e coberturas de oferta e de pedido.",
};

// Label/tooltip/formato/cor de cada KPI — o mock só manda `{ key, value,
// deltaMonth, deltaYear }`, quem decide como exibir é o componente.
const QUOTES_STYLE = {
    total: {
        label: "Total",
        tooltip:
            "Total de cotações criadas no período — soma de em andamento, fechadas e congeladas.",
        format: "int",
        border: BRAND.preto,
    },
    inProgress: {
        label: "Em andamento",
        tooltip:
            "Cotações abertas em coleta de propostas ou negociação, ainda sem decisão de compra.",
        format: "int",
        border: BRAND.laranja,
    },
    closed: {
        label: "Fechadas",
        tooltip: "Cotações encerradas com decisão de compra registrada.",
        format: "int",
        border: BRAND.verde,
    },
    frozen: {
        label: "Congeladas",
        tooltip:
            "Cotações suspensas pelo comprador — paradas sem encerramento nem decisão de compra.",
        format: "int",
        border: BRAND.muted,
        // menos cotações congeladas é uma melhora, por isso diminuir é "bom" aqui
        goodDirection: "down",
    },
};

const QUALITY_STYLE = {
    avgCompetitors: {
        label: "Média concorrentes",
        tooltip: "Média de fornecedores convidados por cotação.",
        format: "decimal2",
    },
    avgProducts: {
        label: "Média produtos",
        tooltip: "Média de itens incluídos por cotação.",
        format: "decimal2",
        // variação do mês pequena demais para ser julgada
        deltaMonthNeutral: true,
    },
    avgOffers: {
        label: "Média oferta",
        tooltip:
            "Média de itens efetivamente ofertados pelos fornecedores por cotação.",
        format: "decimal2",
    },
    sellerEngagement: {
        label: "Engajamento vendedor",
        tooltip:
            "Percentual dos fornecedores convidados que responderam à cotação.",
        format: "percent2",
        deltaStyle: "pctOnly",
        progressColor: BRAND.vermelho,
    },
    offerCoverage: {
        label: "Cobertura de oferta",
        tooltip:
            "Percentual dos itens da cotação que receberam ao menos uma oferta.",
        format: "percent2",
        deltaStyle: "pctOnly",
        progressColor: BRAND.verde,
    },
    orderCoverage: {
        label: "Cobertura de pedido",
        tooltip:
            "Percentual dos itens ofertados que entraram no pedido de compra.",
        format: "percent2",
        deltaStyle: "pctOnly",
        progressColor: BRAND.laranja,
    },
};

export default {
    name: "QuotesSection",

    components: {
        SectionHeader,
        KpiCard,
        KpiTile,
        HighchartsCard,
    },

    props: {
        filters: { type: Object, default: () => ({}) },
    },

    data() {
        return {
            loading: true,
            tooltips: TOOLTIPS,
            quotesKpis: [],
            qualityKpis: [],
            historic: null,
        };
    },

    computed: {
        statusOptions() {
            if (!this.historic) return {};
            const rows = this.historic;
            const theme = baseTheme();
            return {
                ...theme,
                chart: { ...theme.chart, type: "column" },
                xAxis: xAxisDefaults({
                    categories: pluck(rows, "month").map(formatMonthLabel),
                    crosshair: true,
                }),
                yAxis: yAxisDefaults({ title: { text: "Cotações" } }),
                tooltip: { ...theme.tooltip, shared: true },
                plotOptions: {
                    ...theme.plotOptions,
                    column: {
                        stacking: "normal",
                        borderRadius: 2,
                        groupPadding: 0.08,
                    },
                },
                series: [
                    {
                        name: "Fechadas",
                        data: pluck(rows, "closed"),
                        color: BRAND.verde,
                    },
                    {
                        name: "Em andamento",
                        data: pluck(rows, "inProgress"),
                        color: BRAND.laranja,
                    },
                    {
                        name: "Congeladas",
                        data: pluck(rows, "frozen"),
                        color: "#9C948C",
                    },
                ],
            };
        },

        qualityOptions() {
            if (!this.historic) return {};
            const rows = this.historic;
            const theme = baseTheme();

            return {
                ...theme,
                xAxis: xAxisDefaults({
                    categories: pluck(rows, "month").map(formatMonthLabel),
                    crosshair: true,
                }),
                yAxis: [
                    yAxisDefaults({ title: { text: "Médias por cotação" } }),
                    yAxisDefaults({
                        opposite: true,
                        gridLineWidth: 0,
                        min: 0,
                        max: 100,
                        title: { text: "Taxas (%)" },
                        labels: { format: "{value}%" },
                    }),
                ],
                tooltip: { ...theme.tooltip, shared: true },
                plotOptions: {
                    ...theme.plotOptions,
                    column: { borderRadius: 2, groupPadding: 0.06 },
                },
                series: [
                    {
                        type: "column",
                        name: "Média concorrentes",
                        data: pluck(rows, "avgCompetitors"),
                        color: "#FFC1AD",
                    },
                    {
                        type: "column",
                        name: "Média produtos",
                        data: pluck(rows, "avgProducts"),
                        color: "#C5CAE9",
                    },
                    {
                        type: "column",
                        name: "Média oferta",
                        data: pluck(rows, "avgOffers"),
                        color: BRAND.azul,
                    },
                    {
                        type: "spline",
                        name: "Engajamento vendedor",
                        data: pluck(rows, "sellerEngagement"),
                        yAxis: 1,
                        color: BRAND.vermelho,
                        lineWidth: 2,
                        marker: {
                            fillColor: "#fff",
                            lineColor: BRAND.vermelho,
                            lineWidth: 2,
                            radius: 3,
                        },
                        tooltip: { valueSuffix: "%" },
                    },
                    {
                        type: "spline",
                        name: "Cobertura de oferta",
                        data: pluck(rows, "offerCoverage"),
                        yAxis: 1,
                        color: BRAND.verde,
                        lineWidth: 2,
                        dashStyle: "ShortDash",
                        marker: {
                            fillColor: "#fff",
                            lineColor: BRAND.verde,
                            lineWidth: 2,
                            radius: 3,
                        },
                        tooltip: { valueSuffix: "%" },
                    },
                    {
                        type: "spline",
                        name: "Cobertura de pedido",
                        data: pluck(rows, "orderCoverage"),
                        yAxis: 1,
                        color: BRAND.preto,
                        lineWidth: 2,
                        dashStyle: "Dot",
                        marker: {
                            fillColor: "#fff",
                            lineColor: BRAND.preto,
                            lineWidth: 2,
                            radius: 3,
                        },
                        tooltip: { valueSuffix: "%" },
                    },
                ],
            };
        },
    },

    watch: {
        filters: {
            deep: true,
            handler() {
                this.fetch();
            },
        },
    },

    created() {
        this.fetch();
    },

    methods: {
        fetch() {
            this.loading = true;
            service.getCotacoes(this.filters).then(({ data }) => {
                const quotesRaw = data.kpis.filter(
                    (tile) => tile.key in QUOTES_STYLE
                );
                const qualityRaw = data.kpis.filter(
                    (tile) => tile.key in QUALITY_STYLE
                );
                this.quotesKpis = buildKpiTiles(quotesRaw, QUOTES_STYLE);
                this.qualityKpis = buildKpiTiles(qualityRaw, QUALITY_STYLE);
                this.historic = data.historic;
                this.loading = false;
            });
        },
    },
};
</script>
